import React, { ChangeEvent, PureComponent } from 'react';
import {
  ButtonsSectionForSearch,
  FiltersGroup,
  SearchContainer,
  SearchFilters,
  SearchHeader,
  SearchSection,
} from '../Styles/Search';
import { WithTranslation, withTranslation } from 'react-i18next';
import AnimateHeight from 'react-animate-height';
import { searchInMaterials } from '../Actions/Actions';
import SearchPortalProvider from '../../Courses/Components/SearchPortalProvider';
import {
  SearchInput,
  SearchInputIcon,
  ClearInputIcon,
  GoBackButton,
} from '../../Courses/Styles/Styles';
import { smallCloseIcon } from '../../../Assets/images/Icons';
import { closeIcon, searchIcon } from '../../../Assets/images/search';
import { debounce } from 'lodash';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { SearchResultResponse } from '../Types/SearchResultResponse';
import { SearchComponentFilters, SearchMaterial } from '../Types/SearchResult';
import { TransparentButton } from '../../../__core/Styles/Buttons/TransparentButton';
import { arrowIcon } from '../../Courses/Components/Arrow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import SearchPortalMobileProvider from '../../Courses/Components/SearchPortalMobileProvider';
import queryString from 'query-string';
import { getTypeFilterArray } from '../Utils/MaterialFilterMapper';
import {
  filtersInitState,
  FIRST_PAGE,
  ITEMS_ON_PAGE,
  MIN_SEARCH_PHRASE_LENGTH,
  SEARCH_PARAM_NAME,
  searchResultsComponentInitState,
} from '../Utils/Constraints';
import SearchResultItems from './SearchResultItems';
import { Tooltip } from 'react-tippy';
import PortalProvider from '../../../__core/Providers/Portal/PortalProvider';
import { backArrow } from '../../../Assets/images/backArrow';
import { defaultTheme } from '../../../__core/Themes/Default';

type SearchResultsProps = RouteComponentProps &
  WithTranslation & {
    instanceId: string;
  };

type SearchResultsState = {
  searchPhrase: string;
  error: boolean;
  itemsCount: number;
  filters: SearchComponentFilters;
  currentPage: number;
  items: Array<Array<SearchMaterial>>;
  counterIsLoading: boolean;
  resultsIsLoading: boolean;
};

class SearchResults extends PureComponent<
  SearchResultsProps,
  SearchResultsState
> {
  state = searchResultsComponentInitState;
  searchRef = React.createRef<HTMLInputElement>();
  searchSectionRef = React.createRef<HTMLElement>();

  getParamsForRequest = (page: number): string => {
    const { searchPhrase, filters } = this.state;
    const { instanceId } = this.props;

    return queryString.stringify(
      {
        q: searchPhrase,
        perPage: String(ITEMS_ON_PAGE),
        page: String(page),
        instanceId,
        type: getTypeFilterArray(filters),
      },
      { arrayFormat: 'bracket' }
    );
  };

  setSearchParam = (): void => {
    const {
      history: {
        location: { pathname },
        push,
      },
    } = this.props;

    const { searchPhrase } = this.state;
    const currentSearchParam = this.getSearchParam();

    if (searchPhrase) {
      const searchParams = `?${new URLSearchParams({
        search: searchPhrase,
      }).toString()}`;
      if (currentSearchParam !== searchPhrase) {
        push({ pathname, search: searchParams });
      }
    } else {
      push({ pathname });
    }
  };

  loadItems = async (page = FIRST_PAGE): Promise<void> => {
    try {
      const { items } = this.state;
      this.setState({
        counterIsLoading: page === FIRST_PAGE,
        resultsIsLoading: true,
      });

      const response = (await searchInMaterials(
        this.getParamsForRequest(page)
      )) as SearchResultResponse;

      const { all, materials } = response.data;
      const newItems =
        page === FIRST_PAGE ? materials : [...items, ...materials];

      this.setState({
        itemsCount: all,
        currentPage: page,
        items: newItems,
        counterIsLoading: false,
        resultsIsLoading: false,
      });
      this.setSearchParam();
    } catch (e) {
      this.setState({
        error: true,
        counterIsLoading: false,
        resultsIsLoading: false,
      });
    }
  };

  getSearchParam = (): string | null => {
    const { location } = this.props;
    const params = new URLSearchParams(location.search);
    return params.get(SEARCH_PARAM_NAME);
  };

  searchInCourse = debounce(this.loadItems, 500);

  onChangeSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { value },
    } = event;

    this.setState({ searchPhrase: value, currentPage: FIRST_PAGE }, () => {
      if (value && value.length >= MIN_SEARCH_PHRASE_LENGTH) {
        this.searchInCourse();
        if (this.searchRef.current) {
          this.searchRef.current.focus();
        }
      } else {
        this.setState({ items: [], itemsCount: 0 });
      }
    });
  };

  onSearchClear = (): void => {
    this.setState(
      { items: [], itemsCount: 0, searchPhrase: '' },
      this.setSearchParam
    );
  };

  toggleFilter = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { name },
    } = event;

    const newFilters = { ...this.state.filters };

    newFilters[name as keyof SearchComponentFilters] =
      !newFilters[name as keyof SearchComponentFilters];

    const isAllFilterActive = Object.values(newFilters).every((value) => value);

    this.setState(
      { filters: isAllFilterActive ? filtersInitState : newFilters },
      () => this.loadItems()
    );
  };

  showMore = (): void => {
    const { currentPage } = this.state;
    this.loadItems(currentPage + 1);
  };

  getCheckedForAllMaterialsFilter = (): boolean => {
    const { filters } = this.state;

    return !Object.values(filters).find((filterValue) => filterValue);
  };

  renderFilters = (): JSX.Element[] => {
    const { t } = this.props;
    const { filters } = this.state;

    return Object.keys(filters).map((key) => (
      <div
        className={`filter-input${
          this.getCheckedForAllMaterialsFilter() ? ' inactive' : ''
        }`}
        key={key}
      >
        <input
          id={key}
          name={key}
          type="checkbox"
          checked={filters[key as keyof SearchComponentFilters]}
          onChange={this.toggleFilter}
        />
        <label htmlFor={key}>{t(key)}</label>
      </div>
    ));
  };

  setInitFiltersState = (): void => {
    if (!this.getCheckedForAllMaterialsFilter()) {
      this.setState({ filters: filtersInitState }, () => this.loadItems());
    }
  };

  getMaterialUrl = (chapterId: string, materialId: string): string => {
    const { instanceId } = this.props;
    return `/material/${instanceId}/${chapterId}/${materialId}`;
  };

  shouldRenderMoreButton = (): boolean => {
    const { items, itemsCount } = this.state;

    return items.length < itemsCount;
  };

  onScroll = (): void => {
    if (this.searchSectionRef.current) {
      const {
        scrollHeight = 0,
        scrollTop = 0,
        offsetHeight = 0,
      } = this.searchSectionRef.current;

      if (
        scrollTop === scrollHeight - offsetHeight &&
        this.shouldRenderMoreButton()
      ) {
        this.showMore();
      }
    }
  };

  componentDidMount(): void {
    const searchParam = this.getSearchParam();

    if (searchParam) {
      this.setState({ searchPhrase: searchParam }, this.loadItems);
    }

    document.addEventListener('wheel', this.onScroll);
    document.addEventListener('touchmove', this.onScroll);
  }

  componentWillUnmount(): void {
    document.removeEventListener('wheel', this.onScroll);
    document.removeEventListener('touchmove', this.onScroll);
  }

  componentDidUpdate(prevProps: Readonly<SearchResultsProps>): void {
    const { location } = prevProps;
    const params = new URLSearchParams(location.search);
    const prevSearchParam = params.get(SEARCH_PARAM_NAME);
    const currentSearchParam = this.getSearchParam();
    const { searchPhrase } = this.state;

    if (
      currentSearchParam &&
      prevSearchParam !== currentSearchParam &&
      currentSearchParam !== searchPhrase
    ) {
      this.setState({ searchPhrase: currentSearchParam }, this.loadItems);
    }

    if (prevSearchParam !== currentSearchParam && !currentSearchParam) {
      this.setState({ items: [], itemsCount: 0, searchPhrase: '' });
    }
  }

  isMobile = (): boolean =>
    window.matchMedia(`(${defaultTheme.MEDIA_MAX.tablet})`).matches;

  render(): React.ReactNode {
    const {
      searchPhrase,
      itemsCount,
      items,
      counterIsLoading,
      resultsIsLoading,
    } = this.state;
    const { t } = this.props;
    const searchSectionIsVisible =
      searchPhrase.length >= MIN_SEARCH_PHRASE_LENGTH;

    const searchForm = (
      <React.Fragment>
        <form autoComplete="off" onSubmit={(event) => event.preventDefault()}>
          <SearchInput
            ref={this.searchRef}
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            name="search"
            onChange={this.onChangeSearch}
            value={searchPhrase}
            placeholder={t(
              this.isMobile()
                ? 'Enter phrase, min. 3 characters'
                : 'Enter the phrase, minimum 3 characters'
            )}
          />
          <SearchInputIcon>{searchIcon}</SearchInputIcon>
          {searchPhrase && (
            <Tooltip
              position="right"
              html={t('Close the search and go back to previous page')}
            >
              <ClearInputIcon onClick={this.onSearchClear}>
                {closeIcon}
              </ClearInputIcon>
            </Tooltip>
          )}
        </form>
      </React.Fragment>
    );

    return (
      <SearchSection
        visible={searchSectionIsVisible}
        ref={this.searchSectionRef}
      >
        <SearchPortalProvider>{searchForm}</SearchPortalProvider>
        <SearchPortalMobileProvider>{searchForm}</SearchPortalMobileProvider>
        {searchSectionIsVisible && (
          <PortalProvider nodeId="clear-search-portal">
            <GoBackButton as="a" onClick={this.onSearchClear}>
              <Tooltip
                html={t('Close the search and go back to previous page')}
              >
                {backArrow}
                {t('Close search results')}
                {smallCloseIcon}
              </Tooltip>
            </GoBackButton>
          </PortalProvider>
        )}

        <AnimateHeight
          duration={500}
          height={searchSectionIsVisible ? 'auto' : 0}
        >
          <SearchContainer>
            <SearchHeader>
              <h1>
                {t('Search phrase')}:
                <b>
                  {` ${searchPhrase} `}
                  {counterIsLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    `(${itemsCount})`
                  )}
                </b>
              </h1>
              <SearchFilters>
                <div className="label">{t('Search filter')}:</div>
                <FiltersGroup>
                  <div
                    className={`filter-input ${
                      this.getCheckedForAllMaterialsFilter()
                        ? 'all-filters-active'
                        : ''
                    }`}
                  >
                    <input
                      id="all_materials"
                      name="all_materials"
                      type="checkbox"
                      checked={this.getCheckedForAllMaterialsFilter()}
                      onChange={this.setInitFiltersState}
                    />
                    <label htmlFor="all_materials">{t('all materials')}</label>
                  </div>

                  {this.renderFilters()}
                </FiltersGroup>
              </SearchFilters>
            </SearchHeader>
            <SearchResultItems
              items={items}
              getMaterialUrl={this.getMaterialUrl}
            />
            <ButtonsSectionForSearch>
              {resultsIsLoading && (
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              )}
              {this.shouldRenderMoreButton() && (
                <TransparentButton onClick={this.showMore}>
                  {t('Show more')} <span className="arrow">{arrowIcon}</span>
                </TransparentButton>
              )}
            </ButtonsSectionForSearch>
          </SearchContainer>
        </AnimateHeight>
      </SearchSection>
    );
  }
}

export default withRouter(withTranslation()(SearchResults));
